// This compatibility mapper intentionally handles both legacy Apollo failures
// and their tRPC replacements until the remaining form callers are migrated.
// eslint-disable-next-line local-rules/no-graphql-usage
import { isApolloError } from '@apollo/client';
import { isTRPCClientError } from '@trpc/client';
import axios from 'axios';
import type { UseFormSetError } from 'react-hook-form';
import { z } from 'zod';

import { getErrorMessage } from '@/utils/errorUtils';
import { HasuraErrorCodes } from '@/utils/graphqlUtils';

type FormErrorField = 'global' | 'newFiles';

/**
 * tRPC procedures in the file-upload flow. Failures on these map to the
 * `newFiles` form field (V2 parity with the axios `/files/*` branch below).
 * `deleteRelationFiles` is intentionally absent — its V2 twin was an Apollo
 * mutation whose failures surfaced as a global error, not a file-field one.
 * S3 PUT failures stay axios errors and keep their existing handling.
 */
const FILE_UPLOAD_TRPC_PATHS: string[] = [
  'frontend.files.createPresignedUploadUrls',
  'frontend.files.save',
];

/**
 * The CHANGE_REQUEST_REQUIRED cause shape — the wire contract defined by
 * Phase 4a's errorFormatter in `packages/trpc/src/init.ts:
 * WIRE_CAUSE_VALIDATORS`. Both ends of the wire agree on this shape; the
 * frontend reads it via `tryExtractChangeRequestRequired` below.
 *
 * The constant is intentionally inlined rather than imported from
 * `@risksmart-app/approvals` so packages/web doesn't take a workspace
 * dependency on a backend-only package just for a string literal. If this
 * value changes, update Phase 4a's allowlist + this constant + the
 * changeRequestRequiredCauseSchema below together.
 *
 * RSP-1528 §1c. RSP-5788 Phase 6a.
 */
const CHANGE_REQUEST_REQUIRED_ERROR_KIND = 'CHANGE_REQUEST_REQUIRED';

/**
 * The CHANGE_REQUEST_EDIT_NOT_ALLOWED cause shape — the terminal "an in-flight
 * change request exists you may not amend" block surfaced by the tRPC approval
 * helpers (RSP-6513). Whitelisted by the same Phase 4a errorFormatter allowlist
 * in `packages/trpc/src/init.ts: WIRE_CAUSE_VALIDATORS`. Like the CR-required
 * constant above, intentionally inlined rather than imported from
 * `@risksmart-app/approvals` so packages/web takes no backend dependency for a
 * string literal — if this value changes, update the backend allowlist + this
 * constant + the schema below together.
 */
const CHANGE_REQUEST_EDIT_NOT_ALLOWED_ERROR_KIND =
  'CHANGE_REQUEST_EDIT_NOT_ALLOWED';

/**
 * Local `MappedError` discriminant for the change-request-required branch.
 * Distinct from `CHANGE_REQUEST_REQUIRED_ERROR_KIND` (the wire-cause `kind`):
 * this is the frontend-only union tag that `FormContext` switches on.
 */
export const CHANGE_REQUEST_REQUIRED_MAPPED_KIND =
  'change-request-required' as const;

/**
 * Zod schema for the `CHANGE_REQUEST_REQUIRED` cause shape. Used by
 * `tryExtractChangeRequestRequired` to parse-and-narrow the wire cause
 * without an unchecked `as` cast — the parsed result is the typed
 * `ChangeRequestRequiredCause`.
 */
const changeRequestRequiredCauseSchema = z.object({
  kind: z.literal(CHANGE_REQUEST_REQUIRED_ERROR_KIND),
  workflow: z.string(),
  parentId: z.string(),
  requesterCommentRequired: z.boolean(),
});

export type ChangeRequestRequiredCause = z.infer<
  typeof changeRequestRequiredCauseSchema
>;

/**
 * Zod schema for the `CHANGE_REQUEST_EDIT_NOT_ALLOWED` cause shape. Parse-and-
 * narrow (no unchecked `as`) — matches what the backend allowlist forwards.
 */
const changeRequestEditNotAllowedCauseSchema = z.object({
  kind: z.literal(CHANGE_REQUEST_EDIT_NOT_ALLOWED_ERROR_KIND),
  workflow: z.string(),
  parentId: z.string(),
});

export type ChangeRequestEditNotAllowedCause = z.infer<
  typeof changeRequestEditNotAllowedCauseSchema
>;

type MappedError =
  | { kind: 'suppress' }
  | { kind: 'field'; field: FormErrorField; message: string; type?: string }
  | {
      kind: typeof CHANGE_REQUEST_REQUIRED_MAPPED_KIND;
      cause: ChangeRequestRequiredCause;
    }
  | { kind: 'unknown' };

const messageMatchesChangeRequest = (e: Error): boolean =>
  isApolloError(e) &&
  e.graphQLErrors.some((er) =>
    er.message.includes(
      'You need to create a change request to perform this action.'
    )
  );

const messageMatchesAttestationWarning = (e: Error): boolean =>
  e.message.includes('attestation warning error');

/**
 * Extract the typed `CHANGE_REQUEST_REQUIRED` cause from a TRPCClientError,
 * or `null` if the error is not a CR-required signal.
 *
 * The cause shape lives in the wire contract — Phase 4a's errorFormatter
 * is the producer. Without that allowlist on the server, tRPC strips
 * `error.cause` entirely as a security default.
 *
 * Usage:
 *     const cause = tryExtractChangeRequestRequired(error);
 *     if (cause) {
 *       openConfirmModal({ workflow: cause.workflow, parentId: cause.parentId, ... });
 *       return;
 *     }
 *     // ... handle other errors
 */
export const tryExtractChangeRequestRequired = (
  error: unknown
): ChangeRequestRequiredCause | null => {
  if (!isTRPCClientError(error)) {
    return null;
  }
  // Read the formatted cause from `error.shape.data.cause` ONLY — that's
  // the Phase 4a errorFormatter's allowlist-stripped output. Never fall
  // back to the native `error.cause`: that's the unformatted raw cause
  // and could carry internal error metadata the formatter dropped on
  // purpose. If a future refactor clears `shape.data.cause`, this returns
  // null (the intended fail-closed behaviour) rather than silently leaking
  // unwhitelisted fields.
  const cause = error.shape?.data?.cause;

  // Parse-and-narrow against the schema — no unchecked cast. The allowlist
  // on the server (`init.ts:WIRE_CAUSE_VALIDATORS`) only forwards causes
  // that match this shape; this client-side parse is the second line of
  // defence against a malformed wire shape slipping through a misconfigured
  // proxy.
  const parsed = changeRequestRequiredCauseSchema.safeParse(cause);

  return parsed.success ? parsed.data : null;
};

/**
 * Extract the typed `CHANGE_REQUEST_EDIT_NOT_ALLOWED` cause from a
 * TRPCClientError, or `null` if the error is not that block. Reads the
 * allowlist-stripped `error.shape.data.cause` ONLY (same fail-closed contract
 * as `tryExtractChangeRequestRequired` above — never the raw `error.cause`).
 *
 * Unlike CR-required, this is terminal: there's no confirm modal to open, the
 * caller just renders the message. RSP-6513.
 */
export const tryExtractChangeRequestEditNotAllowed = (
  error: unknown
): ChangeRequestEditNotAllowedCause | null => {
  if (!isTRPCClientError(error)) {
    return null;
  }
  const cause = error.shape?.data?.cause;
  const parsed = changeRequestEditNotAllowedCauseSchema.safeParse(cause);

  return parsed.success ? parsed.data : null;
};

export const mapFormError = (error: unknown): MappedError => {
  if (!(error instanceof Error)) {
    return { kind: 'unknown' };
  }

  if (
    messageMatchesChangeRequest(error) ||
    messageMatchesAttestationWarning(error)
  ) {
    return { kind: 'suppress' };
  }

  if (axios.isAxiosError(error) && error.config?.url?.startsWith('/files')) {
    return {
      kind: 'field',
      field: 'newFiles',
      message: getErrorMessage(error) || 'Unable to upload files',
    };
  }

  if (isApolloError(error)) {
    const code = error.graphQLErrors?.[0]?.extensions?.code;

    return {
      kind: 'field',
      field: 'global',
      message: error.message,
      type: typeof code === 'string' ? code : undefined,
    };
  }

  if (isTRPCClientError(error)) {
    // RSP-5788 Phase 6a — the typed CHANGE_REQUEST_REQUIRED branch must
    // come BEFORE the generic global-error fallback below, otherwise the
    // modal flow can't open from a CR-required mutation rejection.
    const crCause = tryExtractChangeRequestRequired(error);
    if (crCause) {
      return { kind: CHANGE_REQUEST_REQUIRED_MAPPED_KIND, cause: crCause };
    }

    // RSP-6513 — the terminal "a change request is already in progress and you
    // may not amend it" block. Render the server's clear message as a global
    // error (no modal, unlike CR-required). Recognising it via the typed cause
    // — rather than letting it fall through to the generic global branch below
    // — keeps the discriminator (`type`) stable instead of a bare 'FORBIDDEN',
    // and gives a single place to localise the copy later.
    const editNotAllowedCause = tryExtractChangeRequestEditNotAllowed(error);
    if (editNotAllowedCause) {
      return {
        kind: 'field',
        field: 'global',
        message: error.message,
        type: CHANGE_REQUEST_EDIT_NOT_ALLOWED_ERROR_KIND,
      };
    }

    // tRPC twin of the axios `/files/*` branch above — file-upload failures
    // belong on the `newFiles` field so FormErrors renders them next to the
    // file input rather than as a generic global error.
    const path = error.data?.path;
    if (typeof path === 'string' && FILE_UPLOAD_TRPC_PATHS.includes(path)) {
      return {
        kind: 'field',
        field: 'newFiles',
        message: error.message || 'Unable to upload files',
      };
    }

    return {
      kind: 'field',
      field: 'global',
      message: error.message,
      type: error.data?.code,
    };
  }

  return { kind: 'unknown' };
};

export const resolvePresentableQueryError = (error: unknown): string | null => {
  const mappedError = mapFormError(error);

  if (
    mappedError.kind === 'suppress' ||
    mappedError.kind === CHANGE_REQUEST_REQUIRED_MAPPED_KIND
  ) {
    return null;
  }

  if (mappedError.kind === 'field') {
    if (
      mappedError.field === 'newFiles' ||
      mappedError.type === CHANGE_REQUEST_EDIT_NOT_ALLOWED_ERROR_KIND
    ) {
      return mappedError.message || 'Unexpected error';
    }

    switch (mappedError.type) {
      case HasuraErrorCodes.PermissionError:
      case 'FORBIDDEN':
        return 'Permission denied';
      case HasuraErrorCodes.ValidationFailed:
      case 'BAD_REQUEST':
      case 'BAD_USER_INPUT':
      case 'GRAPHQL_VALIDATION_FAILED':
      case 'PARSE_ERROR':
      case 'UNPROCESSABLE_CONTENT':
        return 'Validation failed';
      case 'not-found':
      case 'NOT_FOUND':
        return 'Not found';
      case 'invalid-jwt':
      case 'UNAUTHENTICATED':
      case 'UNAUTHORIZED':
        return 'Authentication required';
    }
  }

  return 'Unexpected error';
};

/**
 * react-hook-form's `setError` is typed against the form schema's `FieldPath`,
 * which doesn't include the cross-form sentinels `global` / `newFiles` that
 * `FormErrors` reads. Cast at this single boundary instead of `@ts-ignore` at
 * every call site.
 */
export const setFormFieldError = <TFieldValues extends Record<string, unknown>>(
  setError: UseFormSetError<TFieldValues>,
  field: FormErrorField,
  error: { message: string; type?: string }
): void => {
  (setError as unknown as (name: string, e: typeof error) => void)(
    field,
    error
  );
};
